// Helper function to format file size in human-readable format
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

// Helper function to check if a file is an image
export const isImage = (file: File): boolean => {
  return file.type.startsWith("image/");
};

// Helper function to generate preview for image files
export const generatePreview = async (file: File): Promise<string | null> => {
  if (isImage(file)) {
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.readAsDataURL(file);
    });
  }
  return null;
};

/**
 * The lower-cased extension of `fileName` without its dot, or null when the
 * name carries no usable extension ("scan", "scan.", ".hidden").
 */
export const getFileExtension = (fileName: string): string | null => {
  const base = fileName.trim().split(/[\\/]/).pop() ?? "";
  const dot = base.lastIndexOf(".");

  if (dot <= 0 || dot === base.length - 1) {
    return null;
  }

  return base.slice(dot + 1).toLowerCase();
};

/** `allowed_file_types` entries reduced to bare lower-cased extensions. */
export const normalizeAllowedExtensions = (allowedTypes: string[]): string[] =>
  allowedTypes
    .map((type) => type.trim().toLowerCase().replace(/^\.+/, ""))
    .filter((type) => type.length > 0);

export const isAllowedFileType = (
  fileName: string,
  allowedTypes: string[],
): boolean => {
  const allowed = normalizeAllowedExtensions(allowedTypes);
  if (allowed.length === 0) {
    return true;
  }

  const extension = getFileExtension(fileName);
  return extension !== null && allowed.includes(extension);
};

/**
 * Kept word-for-word in step with the API's rejection message
 * (after-api `helpers/attachments.ts`) so the wording does not change depending
 * on which side catches the file.
 */
export const invalidFileTypeMessage = (
  fileName: string,
  allowedTypes: string[],
): string => {
  const accepted = normalizeAllowedExtensions(allowedTypes)
    .map((type) => `.${type}`)
    .join(", ");
  const extension = getFileExtension(fileName);

  if (!extension) {
    return `This file has no file extension, so we can't tell what type it is. Please upload one of these file types: ${accepted}.`;
  }

  return `.${extension} files aren't accepted here. Please upload one of these file types: ${accepted}.`;
};

// Shared file processing logic
export interface FileProcessOptions {
  maxFileSize: number;
  allowedFileTypes: string[];
  onUploadStateChange: (state: {
    status: "idle" | "uploading" | "success" | "error";
    progress: number;
    error?: string;
  }) => void;
  uploadFile: (file: File) => Promise<{ file_uri: string; file_name: string }>;
  onChange: (value: any) => void;
  onPreviewGenerated?: (previewUrl: string | null) => void;
}

export const processFile = async (
  file: File,
  options: FileProcessOptions,
): Promise<void> => {
  const {
    maxFileSize,
    allowedFileTypes,
    onUploadStateChange,
    uploadFile,
    onChange,
    onPreviewGenerated,
  } = options;

  if (file.size > maxFileSize) {
    onUploadStateChange({
      status: "error",
      progress: 0,
      error: `File size exceeds ${formatFileSize(maxFileSize)} limit, please try resizing or using another file.`,
    });
    return;
  }

  // Every entry point — the file picker, drag-and-drop and paste — funnels
  // through here. The picker's `accept` filter covers only the first of those,
  // and the user can switch it off, so this is the gate that actually holds.
  if (!isAllowedFileType(file.name, allowedFileTypes)) {
    onUploadStateChange({
      status: "error",
      progress: 0,
      error: invalidFileTypeMessage(file.name, allowedFileTypes),
    });
    return;
  }

  try {
    const [previewBlob, response] = await Promise.all([
      generatePreview(file),
      uploadFile(file),
    ]);
    if (onPreviewGenerated) {
      onPreviewGenerated(previewBlob);
    }
    onChange({
      ...response,
      file_size: file.size,
      preview_blob: previewBlob,
    });
  } catch (error) {
    console.error("Error processing file:", error);
  }
};
